/**
 * Custom Column Media Slideshow
 * Handles tab-based slide switching with instant transitions (no animation)
 */
(() => {
  "use strict";

  /**
   * Initialize slideshow for a section
   */
  const initSlideshow = (section) => {
    // Clean up any existing timer from previous initialization
    if (section._autoplayTimer) {
      clearInterval(section._autoplayTimer);
      section._autoplayTimer = null;
    }
    
    const panels = section.querySelectorAll(".c-custom-media-slideshow__panel");
    
    if (!panels.length) return;

    // Get total number of slides from first panel's tabs
    const firstPanel = panels[0];
    const firstPanelTabs = firstPanel.querySelectorAll(".c-custom-media-slideshow__tab");
    const totalSlides = firstPanelTabs.length;

    if (!totalSlides) return;

    // Get autoplay settings
    const autoplayEnabled = section.dataset.autoplayEnabled === "true";
    const autoplayInterval = parseInt(section.dataset.autoplayInterval, 10) || 3000;
    
    let currentSlideIndex = 0;
    let isPaused = false;

    /**
     * Switch to a specific slide
     */
    const switchSlide = (index, skipAutoplayReset = false) => {
      currentSlideIndex = index;
      
      // Update tabs in ALL panels (they all have the same tabs)
      panels.forEach((panel) => {
        const panelTabs = panel.querySelectorAll(".c-custom-media-slideshow__tab");
        panelTabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
          tab.setAttribute("tabindex", isActive ? "0" : "-1");
        });
      });

      // Update panels with smooth transition
      panels.forEach((panel, i) => {
        const isActive = i === index;
        if (isActive) {
          // Remove hidden attribute first to allow transition
          panel.removeAttribute("hidden");
          // Use requestAnimationFrame to ensure the element is rendered before adding active class
          requestAnimationFrame(() => {
            panel.classList.add("is-active");
          });
        } else {
          panel.classList.remove("is-active");
          // Set hidden after transition completes
          setTimeout(() => {
            if (!panel.classList.contains("is-active")) {
              panel.setAttribute("hidden", "");
            }
          }, 500); // Match CSS transition duration
        }
      });

      // Reset autoplay timer if not skipped (only when user manually switches)
      if (!skipAutoplayReset && autoplayEnabled && !isPaused) {
        startAutoplay();
      }
    };

    /**
     * Start autoplay
     */
    const startAutoplay = () => {
      if (!autoplayEnabled || isPaused) return;
      
      // Clear existing timer
      if (section._autoplayTimer) {
        clearInterval(section._autoplayTimer);
        section._autoplayTimer = null;
      }

      // Set up new timer and store it on the section element
      section._autoplayTimer = setInterval(() => {
        if (!isPaused && autoplayEnabled) {
          const nextIndex = (currentSlideIndex + 1) % totalSlides;
          switchSlide(nextIndex, true); // Skip reset to avoid restarting timer
        }
      }, autoplayInterval);
    };

    /**
     * Pause autoplay
     */
    const pauseAutoplay = () => {
      isPaused = true;
      if (section._autoplayTimer) {
        clearInterval(section._autoplayTimer);
        section._autoplayTimer = null;
      }
    };

    /**
     * Resume autoplay after a delay
     */
    const resumeAutoplay = () => {
      isPaused = false;
      // Resume after a delay to give user time to interact
      setTimeout(() => {
        if (!isPaused) {
          startAutoplay();
        }
      }, autoplayInterval);
    };

    /**
     * Handle tab click using event delegation
     */
    section.addEventListener("click", (e) => {
      const tab = e.target.closest(".c-custom-media-slideshow__tab");
      if (!tab) return;

      e.preventDefault();
      pauseAutoplay();
      const tabIndex = parseInt(tab.getAttribute("data-tab-index"), 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < totalSlides) {
        switchSlide(tabIndex);
        resumeAutoplay();
      }
    });

    /**
     * Handle keyboard navigation using event delegation
     */
    section.addEventListener("keydown", (e) => {
      const tab = e.target.closest(".c-custom-media-slideshow__tab");
      if (!tab) return;

      const currentIndex = parseInt(tab.getAttribute("data-tab-index"), 10);
      if (isNaN(currentIndex)) return;

      let newIndex = currentIndex;
      let shouldSwitch = false;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : totalSlides - 1;
          shouldSwitch = true;
          break;

        case "ArrowRight":
          e.preventDefault();
          newIndex = currentIndex < totalSlides - 1 ? currentIndex + 1 : 0;
          shouldSwitch = true;
          break;

        case "Home":
          e.preventDefault();
          newIndex = 0;
          shouldSwitch = true;
          break;

        case "End":
          e.preventDefault();
          newIndex = totalSlides - 1;
          shouldSwitch = true;
          break;
      }

      if (shouldSwitch) {
        pauseAutoplay();
        switchSlide(newIndex);
        // Focus the new tab (from the active panel)
        const activePanel = section.querySelector(".c-custom-media-slideshow__panel.is-active");
        if (activePanel) {
          const newTab = activePanel.querySelector(`[data-tab-index="${newIndex}"]`);
          if (newTab) newTab.focus();
        }
        resumeAutoplay();
      }
    });

    // Pause autoplay on hover (optional - can be removed if not desired)
    section.addEventListener("mouseenter", pauseAutoplay);
    section.addEventListener("mouseleave", () => {
      if (autoplayEnabled) {
        resumeAutoplay();
      }
    });

    // Start autoplay if enabled
    if (autoplayEnabled) {
      startAutoplay();
    }
  };

  /**
   * Initialize all slideshows on the page
   */
  const initAll = () => {
    const sections = document.querySelectorAll(
      "[data-custom-media-slideshow]"
    );

    sections.forEach((section) => {
      initSlideshow(section);
    });
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Re-initialize on section load (for theme editor)
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.detail.querySelector("[data-custom-media-slideshow]");
    if (section) {
      initSlideshow(section);
    }
  });

  // Re-initialize on section select (for theme editor)
  document.addEventListener("shopify:section:select", (event) => {
    const section = event.detail.querySelector("[data-custom-media-slideshow]");
    if (section) {
      initSlideshow(section);
    }
  });
})();
